// Shared texts of the account deletion screens (settings + public /delete-account page).
// lang = 'fr' (default, public page) or 'en' (Settings in English, deletionCopy.en.js).
import * as EN from './deletionCopy.en';

const isEn = (lang) => lang === 'en';
const ERRORS = {
  code_invalid: 'Code invalide ou expiré.',
  code_too_many_attempts: 'Trop de tentatives. Demandez un nouveau code.',
  code_rate_limited: 'Trop de codes demandés. Réessayez dans une heure.',
  code_email_failed: "L'e-mail n'a pas pu être envoyé. Réessayez.",
  password_invalid: 'Mot de passe incorrect.',
  already_requested: 'Une demande de suppression est déjà en cours.',
  admin_account: "Un compte d'administration doit d'abord céder ses droits : utilisez « Demander le retrait de mes droits ».",
  owner_account: "Le compte propriétaire ne peut pas être supprimé tant que la propriété de la plateforme n'a pas été transférée.",
  not_admin: "Ce compte n'a pas de droits d'administration.",
  no_open_request: 'Aucune demande en cours.',
  session_expired: 'Votre vérification a expiré. Recommencez.',
};

export function deletionError(err, lang = 'fr') {
  const d = err?.response?.data?.detail;
  const E = isEn(lang) ? EN.ERRORS : ERRORS;
  if (d?.code && E[d.code]) return E[d.code];
  if (typeof d === 'string' && !isEn(lang)) return d;
  return isEn(lang) ? EN.GENERIC : 'Une erreur est survenue. Réessayez.';
}

const BLOCKERS = {
  token_balance: 'Solde en jetons',
  wallet_balance: 'Solde du portefeuille',
  staking_balance: 'Solde de staking',
  active_staking: 'Position de staking active',
  pending_withdrawal: 'Retrait en cours',
  pending_deposit: 'Dépôt en cours de confirmation',
  active_hold: 'Fonds réservés',
  open_escrow: 'Commande Marketplace en séquestre ou en litige',
};

const ACTIONS = {
  token_balance: 'Retirez ou échangez ce solde depuis le Wallet.',
  wallet_balance: 'Retirez ce solde depuis le Wallet.',
  staking_balance: 'Retirez ce solde depuis JAPAP Staking.',
  active_staking: "Attendez l'échéance ou débloquez la position depuis JAPAP Staking.",
  pending_withdrawal: 'Attendez la fin du retrait.',
  pending_deposit: 'Attendez la confirmation du dépôt.',
  active_hold: "Attendez la fin de l'opération concernée.",
  open_escrow: 'Terminez la commande ou le litige sur la Marketplace.',
};

export const blockerLabel = (b, lang = 'fr') => `${(isEn(lang) ? EN.BLOCKERS : BLOCKERS)[b.code] || b.code}${b.amount ? `${isEn(lang) ? ':' : ' :'} ${b.amount} ${b.symbol || ''}` : b.count ? ` (${b.count})` : ''}`;
export const blockerAction = (b, lang = 'fr') => (isEn(lang) ? EN.ACTIONS[b.code] || EN.CONTACT_SUPPORT : ACTIONS[b.code] || 'Contactez le support.');

export const fmtDate = (iso, lang = 'fr') => (iso ? new Date(iso).toLocaleDateString(isEn(lang) ? 'en-GB' : 'fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '');

export const policySections = (graceDays, securityDays = 365, lang = 'fr') => (isEn(lang) ? EN.policy(graceDays, securityDays) : [
  {
    id: 'delay',
    title: 'Délai et annulation',
    items: [
      `Après confirmation, JAPAP attend ${graceDays} jours avant la suppression définitive. Ce délai d'annulation est une règle JAPAP, réglable par JAPAP ; ce n'est pas une exigence de Google.`,
      "L'annulation demande une action explicite (bouton « Annuler la suppression ») : se reconnecter n'annule rien.",
      'Toutes vos sessions sont fermées et les notifications de vos appareils sont désactivées immédiatement.',
      'Pendant ce délai, vous pouvez vous reconnecter pour annuler la demande ou régler vos fonds.',
    ],
  },
  {
    id: 'funds',
    title: 'Vos fonds restent à vous',
    items: [
      "Aucun solde n'est mis à zéro, transféré, confisqué ou abandonné.",
      'Si un solde, une position de staking, un retrait, un dépôt ou une commande en séquestre est en cours, la suppression est différée avec le motif et les démarches à suivre.',
      'Un solde trop petit pour être retiré se règle avec le support, sans renonciation imposée.',
    ],
  },
  {
    id: 'deleted',
    title: 'Ce qui est supprimé',
    items: [
      'Votre profil : nom, pseudo, e-mail, téléphone, photo, couverture, bio et informations professionnelles.',
      'Vos publications, commentaires, réactions, stories, reels, vidéos, annonces sans commande et les médias associés.',
      'Vos abonnements, abonnés, contacts synchronisés, notifications, préférences, appareils, sessions et historiques de jeux.',
    ],
  },
  {
    id: 'messages',
    title: 'Messages déjà envoyés',
    items: [
      'Le texte et les pièces jointes de vos messages sont effacés de nos serveurs et remplacés par « Message supprimé ».',
      "La conversation reste visible pour les autres participants, avec l'auteur affiché « Utilisateur supprimé », sans lien vers un profil.",
      'Les copies que les destinataires ont enregistrées eux-mêmes (captures, téléchargements, transferts) ne sont pas sous le contrôle de JAPAP.',
    ],
  },
  {
    id: 'kept',
    title: 'Ce qui est conservé, et pourquoi',
    items: [
      'Données financières (registre du portefeuille, transactions, dépôts, retraits, staking, commandes) : 10 ans, pour les obligations comptables et fiscales. Les montants restent ; à l\'issue, les coordonnées liées aux opérations (numéro Mobile Money, adresse, nom) sont effacées.',
      'Vérification d\'identité (KYC) : 5 ans après la suppression du compte, durée exigée par la réglementation de lutte contre le blanchiment (UK Money Laundering Regulations 2017), puis effacement des documents.',
      'Modération, sanctions et demandes au support liées à la suppression : 1 an, pour traiter les recours, litiges et demandes des autorités, puis effacement.',
      `Journaux de sécurité et antifraude (adresses IP, appareils, accès refusés) : ${securityDays} jours, pour enquêter sur une fraude ou un piratage de compte et répondre aux autorités, puis effacement.`,
      'Ces éléments ne sont plus rattachés à vos nom, e-mail ou téléphone, mais à un identifiant interne : ils sont pseudonymisés (pas anonymes), leur accès est restreint et l\'effacement à l\'échéance est automatique.',
    ],
  },
]);

const UI = {
  fr: {
    claim_done: (r) => `Demande transmise au support (réf. ${r}). Aucun solde n'est abandonné.`,
    claim_open: 'Un solde ne peut pas être retiré ? Demander son règlement au support',
    claim_ph: 'Précisez le solde concerné et comment vous souhaitez le récupérer.',
    claim_send: 'Envoyer au support',
    hold_title: 'Suppression différée : des fonds ou engagements restent à régler',
    pending_title: 'Suppression de compte programmée',
    dates: (a, b) => `Demandée le ${a} · suppression définitive à partir du ${b}`,
    hold_end: ', dès que tout est réglé.',
    cancel: 'Annuler la suppression et garder mon compte',
    blockers_intro: 'Vous pouvez enregistrer la demande. La suppression définitive sera différée tant que ces éléments ne sont pas réglés :',
    agree: "J'ai lu ce qui sera supprimé et conservé, et je veux supprimer mon compte JAPAP.",
    send_code: (m) => `Recevoir un code par e-mail (${m})`,
    code_sent: (m) => `Code envoyé à ${m}`,
    code_ph: 'Code à 6 chiffres',
    code_label: 'Code de vérification reçu par e-mail',
    password_ph: 'Mot de passe du compte',
    confirm: 'Supprimer mon compte',
    resend: 'Renvoyer le code',
    no_email: 'Plus accès à votre e-mail ? Utilisez le recours sécurisé sur la page',
    owner: "Compte propriétaire : il ne peut pas être supprimé tant que la propriété de la plateforme n'a pas été transférée à un autre compte.",
    admin: "Compte d'administration : la suppression s'ouvre après le retrait de vos droits. Le propriétaire réattribue vos responsabilités, retire votre rôle (action tracée), puis vous pouvez demander la suppression ici.",
    handover_done: (r) => `Demande transmise (réf. ${r}).`,
    handover: 'Demander le retrait de mes droits',
    cancelled: 'Suppression annulée. Votre compte est conservé.',
    requested: 'Demande enregistrée. Toutes vos sessions ont été fermées.',
    loading: 'Chargement…',
    page_title: 'Supprimer mon compte JAPAP',
    page_seo: "Demandez la suppression de votre compte JAPAP et de vos données, sans l'application.",
    page_intro: "Cette page permet de demander la suppression de votre compte et de vos données sans l'application ni votre ancien mot de passe.",
    page_in_app: "Dans l'application : Profil → Paramètres → Mon compte → Supprimer mon compte.",
    page_requested: 'Votre demande est enregistrée et vos sessions sont fermées. Un e-mail de confirmation vous a été envoyé. Pour annuler, reconnectez-vous ou vérifiez votre adresse ci-dessous.',
    email_label: 'Adresse e-mail de votre compte JAPAP',
    email_ph: 'vous@exemple.com',
    captcha_label: 'Vérification rapide',
    captcha_helper: 'Résolvez le calcul pour continuer',
    get_code: 'Recevoir un code',
    code_sent_public: "Si un compte JAPAP existe avec cette adresse, un code à 6 chiffres vient d'y être envoyé. Il est valable 10 minutes.",
    verify: 'Vérifier',
    restart: "Changer d'adresse ou renvoyer un code",
    verified: (m) => `Compte vérifié : ${m}`,
    extra_label: 'Vérification supplémentaire : mot de passe du compte',
    extra_why: 'Demandé parce que des fonds sont rattachés au compte. Mot de passe oublié ?',
    extra_reset: 'Réinitialisez-le',
    extra_or: 'ou utilisez le recours support ci-dessous.',
    support_toggle: "Je n'ai plus accès à l'adresse e-mail du compte",
    support_done: "Demande reçue. Le support vérifiera votre identité avant toute action et vous répondra à l'adresse de contact indiquée. Rien n'est supprimé automatiquement.",
    support_email: 'Adresse e-mail du compte',
    support_contact: 'Adresse où vous répondre',
    support_name: 'Nom affiché sur le compte',
    support_msg: "Éléments permettant de vérifier que le compte est le vôtre (téléphone, date d'inscription…)",
    details: 'Détails :',
    policy_link: 'politique de confidentialité',
  },
  en: {
    claim_done: (r) => `Request sent to support (ref. ${r}). No balance is forfeited.`,
    claim_open: 'A balance cannot be withdrawn? Ask support to settle it',
    claim_ph: 'Specify the balance concerned and how you would like to recover it.',
    claim_send: 'Send to support',
    hold_title: 'Deletion deferred: funds or commitments still need to be settled',
    pending_title: 'Account deletion scheduled',
    dates: (a, b) => `Requested on ${a} · final deletion from ${b}`,
    hold_end: ', once everything is settled.',
    cancel: 'Cancel deletion and keep my account',
    blockers_intro: 'You can record the request. Final deletion will be deferred until these items are settled:',
    agree: 'I have read what will be deleted and kept, and I want to delete my JAPAP account.',
    send_code: (m) => `Get a code by email (${m})`,
    code_sent: (m) => `Code sent to ${m}`,
    code_ph: '6-digit code',
    code_label: 'Verification code received by email',
    password_ph: 'Account password',
    confirm: 'Delete my account',
    resend: 'Resend the code',
    no_email: 'No longer have access to your email? Use the secure option on the page',
    owner: 'Owner account: it cannot be deleted until ownership of the platform has been transferred to another account.',
    admin: 'Administration account: deletion opens once your rights have been removed. The owner reassigns your responsibilities and removes your role (logged action), then you can request deletion here.',
    handover_done: (r) => `Request sent (ref. ${r}).`,
    handover: 'Request removal of my rights',
    cancelled: 'Deletion cancelled. Your account is kept.',
    requested: 'Request recorded. All your sessions have been closed.',
    loading: 'Loading…',
    page_title: 'Delete my JAPAP account',
    page_seo: 'Request the deletion of your JAPAP account and your data, without the app.',
    page_intro: 'This page lets you request the deletion of your account and your data without the app or your old password.',
    page_in_app: 'In the app: Profile → Settings → My account → Delete my account.',
    page_requested: 'Your request is recorded and your sessions are closed. A confirmation email has been sent to you. To cancel, sign in again or verify your address below.',
    email_label: 'Email address of your JAPAP account',
    email_ph: 'you@example.com',
    captcha_label: 'Quick check',
    captcha_helper: 'Solve the sum to continue',
    get_code: 'Get a code',
    code_sent_public: 'If a JAPAP account exists with this address, a 6-digit code has just been sent to it. It is valid for 10 minutes.',
    verify: 'Verify',
    restart: 'Change address or resend a code',
    verified: (m) => `Account verified: ${m}`,
    extra_label: 'Additional check: account password',
    extra_why: 'Requested because funds are linked to the account. Forgot your password?',
    extra_reset: 'Reset it',
    extra_or: 'or use the support option below.',
    support_toggle: 'I no longer have access to the account email address',
    support_done: 'Request received. Support will verify your identity before taking any action and will reply to the contact address you gave. Nothing is deleted automatically.',
    support_email: 'Account email address',
    support_contact: 'Address where we should reply',
    support_name: 'Name shown on the account',
    support_msg: 'Information proving the account is yours (phone number, sign-up date…)',
    details: 'Details:',
    policy_link: 'privacy policy',
  },
};
export const deletionUi = (lang = 'fr') => UI[isEn(lang) ? 'en' : 'fr'];
